/**
 * Alt text generation via Google Gemini — strictly BYO-key.
 *
 * The API key is never bundled or baked in at build time. It is stored
 * only in localStorage under `altifier.geminiKey` and is used solely in
 * direct calls to Google's API. If no key is set, generation is disabled
 * (callers should hide Generate buttons); manual alt-text entry remains
 * fully functional.
 */

const GEMINI_KEY_STORAGE = 'altifier.geminiKey';

// Model used for generation. NOTE: README must match whatever lives here —
// do not change one without the other (ADR 2026-10-16: keep the model that
// has been live-tested; bump deliberately with a live generation check).
const GEMINI_MODEL = 'gemini-1.5-flash';
const GEMINI_ENDPOINT = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

/** @returns {string} The user's Gemini API key, or '' if not set. */
export function getGeminiKey() {
  try {
    return (typeof localStorage !== 'undefined' && localStorage.getItem(GEMINI_KEY_STORAGE)) || '';
  } catch {
    return '';
  }
}

/** @returns {boolean} Whether a Gemini API key is configured. */
export function isGeminiConfigured() {
  return getGeminiKey().trim().length > 0;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;

      if (result === null) {
        reject(new Error('Failed to read image data.'));

        return;
      }

      // readAsDataURL always yields a string (FileReader spec) — strip the
      // data URL prefix, e.g. "data:image/jpeg;base64,".
      resolve(result.slice(result.indexOf(',') + 1));
    };

    reader.onerror = () => reject(new Error('Failed to read image data.'));
    reader.readAsDataURL(blob);
  });
}

/**
 * Generates alt text for an image following Section 508 guidelines.
 *
 * @param {string} imageUrl - URL of the image to describe
 * @param {string} postText - Text of the surrounding post (for context)
 * @returns {Promise<string>} - Generated alt text
 */
export async function generateAltText(imageUrl, postText = '') {
  const key = getGeminiKey().trim();

  if (!key) {
    throw new Error('No Gemini API key set. Add one in Settings to enable generation.');
  }

  if (!imageUrl) {
    throw new Error('This media has no viewable image to describe.');
  }

  // Fetch the image and send it inline to Gemini (no upload step needed).
  const imageResp = await fetch(imageUrl);

  if (!imageResp.ok) {
    throw new Error(`Could not fetch the image (HTTP ${imageResp.status}).`);
  }

  const blob = await imageResp.blob();
  const base64 = await blobToBase64(blob);
  const mimeType = blob.type || 'image/jpeg';

  const prompt =
    'Write concise, accurate alternative text for this image following Section 508 ' +
    'guidelines. Describe the essential visual content in plain language. ' +
    (postText
      ? `Here is the surrounding post text for context: "${postText.slice(0, 500)}". ` +
        'Avoid repeating text already present. '
      : '') +
    'Respond with only the alt text, no preamble or quotes.';

  // The key rides in the x-goog-api-key header, never the URL — query
  // parameters leak into proxies and server logs.
  const resp = await fetch(GEMINI_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-goog-api-key': key,
    },
    body: JSON.stringify({
      contents: [
        {
          parts: [
            { text: prompt },
            { inlineData: { mimeType, data: base64 } },
          ],
        },
      ],
      generationConfig: { maxOutputTokens: 500 },
    }),
  });

  if (!resp.ok) {
    let message = `Gemini request failed (HTTP ${resp.status}).`;

    try {
      const errData = await resp.json();

      if (errData?.error?.message) message = errData.error.message;
    } catch {
      // ignore parse failure, keep the generic message
    }

    throw new Error(message);
  }

  const data = await resp.json();

  const text = (data?.candidates?.[0]?.content?.parts || [])
    .map((p) => p?.text || '')
    .join('')
    .trim();

  if (!text) throw new Error('Gemini returned no alt text.');

  return text;
}

/**
 * Batch-generate alt text for multiple images.
 * Returns a map of image index -> structured result, so callers can
 * distinguish generated text from failures (failure strings must never be
 * applied as alt text).
 *
 * @param {Array} images - Array of image objects with cdnUrl
 * @param {string} postText - Surrounding post text
 * @returns {Promise<Object<number, {ok: boolean, altText?: string, error?: string}>>}
 */
export async function batchGenerateAltText(images, postText = '') {
  const results = {};

  for (const img of images) {
    try {
      const altText = await generateAltText(img.cdnUrl, postText);
      results[img.index] = { ok: true, altText };
    } catch (err) {
      results[img.index] = { ok: false, error: err.message || String(err) };
    }
  }

  return results;
}
